import React, {useCallback, useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {mapEventChannel} from 'events/map'
import {
  MapButtonPosition,
  useChangeMapLayout
} from 'hooks/useChangeMapLayout/useChangeMapLayout'
import {useScrollPosition} from 'hooks/useScrollPosition'
import {getUserAgentDetails} from 'modules/meta/selectors'
import {getHotelId, getIsUserSearch} from 'modules/search/selectors'
import {MapDisplayAction} from 'modules/search/types'
import {getIsSearchBoxOpen} from 'modules/searchBox/selectors'
import {AnimatePresence, motion} from 'motion/react'
import getSafeBottomSpacingValue from 'utils/getSafeBottomSpacing'

import {Button, LabelWrapper as AtlasLabelWrapper} from '@vio/atlas/Button'
import {LabelWrapper as NewStyleLabelWrapper} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const Wrapper = styled(motion.div)<{isSafari: boolean}>`
  position: fixed;
  z-index: 10;
  html[dir='rtl'] & {
    right: 50%;
  }
  html[dir='ltr'] & {
    left: 50%;
  }
  bottom: ${({isSafari}) =>
    getSafeBottomSpacingValue({
      spacing: cssTheme.layout.spacing.s500,
      isSafari
    })};
`

const FloatingButton = styled(Button)`
  ${AtlasLabelWrapper}, ${NewStyleLabelWrapper} {
    padding: 0;
    display: inline-block;
  }

  ${AtlasLabelWrapper}:first-letter, ${NewStyleLabelWrapper}:first-letter {
    text-transform: uppercase;
  }
`

const MessageWrapper = styled.span`
  padding: 0 ${cssTheme.layout.spacing.s300};
  white-space: nowrap;
`

export const ShowMapFloatingButton = () => {
  const isSafari = useSelector(getUserAgentDetails)?.isSafari // User details is not available server side
  const changeMapLayout = useChangeMapLayout()
  const searchBoxIsOpen = useSelector(getIsSearchBoxOpen)
  const hotelIdFromUrl = useSelector(getHotelId)
  const isUserSearch = useSelector(getIsUserSearch)
  const [isFooterInView, setIsFooterInView] = useState<boolean>(false)
  useEffect(() => {
    const handleFooterInView = (inView: boolean) => {
      setIsFooterInView(inView)
    }

    mapEventChannel.on('footerInView', handleFooterInView)

    return () => {
      mapEventChannel.off('footerInView', handleFooterInView)
    }
  }, [])
  const dispatch = useDispatch()

  const {scrollY} = useScrollPosition()

  const onClick = useCallback(() => {
    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.MapViewButton,
        action: Action.Clicked,
        component: MapButtonPosition.Floating
      })
    )
    changeMapLayout(MapDisplayAction.SHOW, false)
  }, [changeMapLayout, dispatch])

  const shouldShowButton = () => {
    if (searchBoxIsOpen) return false
    if (hotelIdFromUrl && !isUserSearch && scrollY <= 0) return false
    if (isFooterInView) return false
    return true
  }

  return (
    <ShowMapFloatingButtonComponent
      onClick={onClick}
      isSafari={isSafari}
      shouldShowButton={shouldShowButton()}
    />
  )
}

interface Props {
  onClick: () => void
  isSafari: boolean
  shouldShowButton: boolean
}

const ShowMapFloatingButtonComponent = React.memo(
  function ShowMapFloatingButtonComponent({
    onClick,
    isSafari,
    shouldShowButton
  }: Props) {
    return (
      <AnimatePresence>
        {shouldShowButton && (
          <Wrapper
            key="show-map-floating-button"
            isSafari={isSafari}
            initial={{opacity: 0, y: '200%', x: '-50%'}}
            animate={{opacity: 1, y: 0, x: '-50%'}}
            exit={{opacity: 0, y: '200%', x: '-50%'}}
          >
            <FloatingButton
              variant="inverse"
              isRounded
              size="xl"
              iconStart={<Icon name="Map" />}
              onClick={onClick}
              isFloating
              dataId="mobileMapButton"
            >
              <MessageWrapper>
                <FormattedMessageWrapper id="map" defaultMessage="Map" />
              </MessageWrapper>
            </FloatingButton>
          </Wrapper>
        )}
      </AnimatePresence>
    )
  }
)
